<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">申请审核管理</h2>
          <p class="text-sm text-neutral-500 mt-1">
            审核新用户注册申请，处理用户提交的工单申请
          </p>
        </div>
        <!-- Tab 切换 -->
        <div class="flex gap-2 bg-neutral-100 rounded-lg p-1">
          <button
            v-for="t in tabs"
            :key="t.id"
            @click="switchTab(t.id)"
            class="px-4 py-2 rounded-md text-sm font-medium transition-colors"
            :class="currentTab === t.id ? 'bg-white text-primary shadow-sm' : 'text-neutral-500 hover:text-neutral-700'"
          >
            <i :class="'fa ' + t.icon + ' mr-1'"></i>{{ t.name }}
          </button>
        </div>
      </div>

      <!-- 筛选栏 -->
      <div class="flex flex-wrap items-center gap-3 mb-5">
        <div class="flex gap-1 bg-neutral-100 rounded-lg p-1">
          <button
            v-for="s in statusOptions"
            :key="s.value"
            @click="changeStatus(s.value)"
            class="px-3 py-1.5 rounded-md text-sm transition-colors"
            :class="status === s.value ? 'bg-white text-primary shadow-sm font-medium' : 'text-neutral-500 hover:text-neutral-700'"
          >
            {{ s.label }}
          </button>
        </div>
        <div class="relative flex-1 min-w-[200px] max-w-xs">
          <input
            type="text"
            v-model="searchKey"
            @input="handleSearch"
            :placeholder="currentTab === 'register' ? '搜索姓名/用户名/手机号/学校...' : '搜索申请人/标题...'"
            class="w-full px-4 py-2 pl-10 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
          <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
        </div>
        <button
          @click="refreshList"
          class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
          title="刷新列表"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>

      <!-- ===== 注册审核列表（两列并排） ===== -->
      <div v-if="currentTab === 'register'" v-loading="loading" class="grid grid-cols-1 xl:grid-cols-2 gap-4" style="min-height: 120px">
        <div
          v-for="u in list"
          :key="u.uid"
          class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-5 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex flex-col sm:flex-row sm:items-center gap-4">
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <div class="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold">
                {{ (u.name || '?').charAt(0) }}
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-bold text-neutral-900">{{ u.name }}</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full"
                    :class="{
                      'bg-orange-100 text-orange-700': u.status === 0,
                      'bg-green-100 text-green-700': u.status === 1,
                      'bg-red-100 text-red-700': u.status === 2,
                    }"
                  >
                    {{ statusText(u.status) }}
                  </span>
                </div>
                <div class="text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-4">
                  <span v-if="u.loginId"><i class="fa fa-user-o mr-1"></i>{{ u.loginId }}</span>
                  <span v-if="u.phone"><i class="fa fa-phone mr-1"></i>{{ u.phone }}</span>
                  <span v-if="u.schName"><i class="fa fa-building-o mr-1"></i>{{ u.schName }}</span>
                  <span><i class="fa fa-clock-o mr-1"></i>{{ u.createTime }}</span>
                </div>
                <div class="text-xs text-neutral-400 mt-1 flex flex-wrap gap-x-4">
                  <span v-if="u.province">{{ u.province }} {{ u.city }} {{ u.district }}</span>
                  <span v-if="u.stage">学段：{{ u.stage }}</span>
                  <span v-if="u.subject">学科：{{ u.subject }}</span>
                </div>
              </div>
            </div>
            <div v-if="u.status === 0" class="flex gap-2 shrink-0">
              <button
                @click="reviewRegister(u, 1)"
                class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <i class="fa fa-check mr-1"></i>通过
              </button>
              <button
                @click="reviewRegister(u, 2)"
                class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
              >
                <i class="fa fa-times mr-1"></i>驳回
              </button>
            </div>
          </div>
        </div>
        <div v-if="!loading && list.length === 0" class="text-center py-12 text-neutral-400 col-span-full">
          <i class="fa fa-user-plus text-4xl mb-3"></i>
          <p>暂无相关记录</p>
        </div>
      </div>

      <!-- ===== 申请工单列表（两列并排） ===== -->
      <div v-if="currentTab === 'apply'" v-loading="loading" class="grid grid-cols-1 xl:grid-cols-2 gap-4" style="min-height: 120px">
        <div
          v-for="a in list"
          :key="a.id"
          class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-5 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex flex-col sm:flex-row sm:items-start gap-4">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-bold text-neutral-900">{{ a.title }}</span>
                <span class="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  {{ typeName(a.type) }}
                </span>
                <span
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="{
                    'bg-orange-100 text-orange-700': a.status === 0,
                    'bg-green-100 text-green-700': a.status === 1,
                    'bg-red-100 text-red-700': a.status === 2,
                  }"
                >
                  {{ statusText(a.status) }}
                </span>
              </div>
              <div class="text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-4">
                <span><i class="fa fa-user-o mr-1"></i>{{ a.userName }}</span>
                <span><i class="fa fa-clock-o mr-1"></i>{{ a.createTime }}</span>
              </div>
              <p class="text-sm text-neutral-700 mt-2 whitespace-pre-wrap break-all">{{ a.content }}</p>
              <div v-if="a.status !== 0 && (a.reply || a.reviewerName)" class="mt-3 bg-white border border-neutral-200 rounded-lg p-3 text-sm">
                <div class="text-neutral-400 text-xs mb-1">
                  审核回复 · {{ a.reviewerName || '-' }} · {{ a.reviewTime || '-' }}
                </div>
                <div class="text-neutral-700 whitespace-pre-wrap break-all">{{ a.reply || '（无回复内容）' }}</div>
              </div>
            </div>
            <div class="flex flex-col gap-2 shrink-0">
              <button
                @click="openChat(a)"
                class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <i class="fa fa-comments-o mr-1"></i>对话回复
              </button>
              <div v-if="a.status === 0" class="flex gap-2">
                <button
                  @click="reviewApply(a, 1)"
                  class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  <i class="fa fa-check mr-1"></i>通过
                </button>
                <button
                  @click="reviewApply(a, 2)"
                  class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                >
                  <i class="fa fa-times mr-1"></i>驳回
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!loading && list.length === 0" class="text-center py-12 text-neutral-400 col-span-full">
          <i class="fa fa-file-text-o text-4xl mb-3"></i>
          <p>暂无相关记录</p>
        </div>
      </div>

      <!-- 分页 -->
      <div class="flex items-center justify-between pt-4 col-span-full" v-if="total > 0">
        <span class="text-sm text-neutral-500">共 {{ total }} 条记录</span>
        <div class="flex gap-2">
          <button
            :disabled="page <= 1"
            @click="changePage(page - 1)"
            class="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
          >
            上一页
          </button>
          <span class="px-3 py-1.5 text-sm text-neutral-600">{{ page }} / {{ totalPages }}</span>
          <button
            :disabled="page >= totalPages"
            @click="changePage(page + 1)"
            class="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
          >
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- ===== 工单对话弹窗 ===== -->
    <el-dialog
      :title="chat.apply ? '对话 · ' + chat.apply.title : '对话'"
      v-model="chatVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="text-xs text-neutral-400 mb-3" v-if="chat.apply">
        <i class="fa fa-user-o mr-1"></i>{{ chat.apply.userName }}
        <span class="ml-3">{{ typeName(chat.apply.type) }}</span>
        <span class="ml-3">{{ statusText(chat.apply.status) }}</span>
      </div>

      <div class="max-h-[50vh] overflow-y-auto space-y-3 pr-1" ref="chatBox">
        <div
          v-for="(m, i) in chat.list"
          :key="i"
          class="flex"
          :class="m.role === 'admin' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[78%] rounded-2xl px-4 py-2.5 text-sm"
            :class="
              m.role === 'admin'
                ? 'bg-primary text-white rounded-br-md'
                : 'bg-neutral-100 text-neutral-800 rounded-bl-md'
            "
          >
            <div
              v-if="m.role !== 'admin'"
              class="text-xs text-neutral-500 font-medium mb-1"
            >
              {{ m.senderName || '用户' }}
            </div>
            <div class="whitespace-pre-wrap break-words">{{ m.content }}</div>
            <div
              class="text-[11px] mt-1"
              :class="m.role === 'admin' ? 'text-white/70' : 'text-neutral-400'"
            >
              {{ m.createTime }}
            </div>
          </div>
        </div>
        <div v-if="chat.list.length === 0" class="text-center text-neutral-400 text-sm py-8">
          暂无对话内容
        </div>
      </div>

      <div class="mt-4 flex gap-2">
        <input
          v-model="chatInput"
          @keyup.enter="sendReply"
          maxlength="1000"
          placeholder="输入回复内容，用户会收到站内信提醒…"
          class="flex-1 px-4 py-2.5 rounded-lg border border-neutral-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
        />
        <button
          @click="sendReply"
          :disabled="chatSending"
          class="px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {{ chatSending ? '发送中' : '回复' }}
        </button>
      </div>

      <div class="mt-3 flex justify-between items-center">
        <span class="text-xs text-neutral-400">回复后用户端「消息中心」会立即收到通知</span>
        <button @click="closeChat" class="text-xs text-neutral-400 hover:text-red-500">
          <i class="fa fa-lock mr-1"></i>结束对话
        </button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "ApplyManager",
  data() {
    return {
      currentTab: "register",
      tabs: [
        { id: "register", name: "注册审核", icon: "fa-user-plus" },
        { id: "apply", name: "申请工单", icon: "fa-file-text-o" },
      ],
      statusOptions: [
        { label: "待审核", value: 0 },
        { label: "已通过", value: 1 },
        { label: "已驳回", value: 2 },
        { label: "全部", value: -1 },
      ],
      // 工单对话
      chatVisible: false,
      chat: { apply: null, list: [] },
      chatInput: "",
      chatSending: false,
      status: 0,
      searchKey: "",
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      loading: false,
      typeMap: {
        publish: "发布工具权限",
        points: "创值申诉",
        account: "账号问题",
        other: "其他",
      },
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
  },
  created() {
    this.getList();
  },
  methods: {
    statusText(s) {
      return { 0: "待审核", 1: "已通过", 2: "已驳回" }[s] || "未知";
    },
    // ===== 工单对话（平台 ↔ 用户）=====
    async openChat(a) {
      try {
        const res = await this.$axios({
          url: "notice/admin/apply/msgs",
          method: "POST",
          data: { data: await this.$aesEncrypt({ applyId: a.id }) },
        });
        if (res.code === 200 && res.data) {
          this.chat = res.data;
          this.chatInput = "";
          this.chatVisible = true;
          this.scrollChat();
        }
      } catch (e) {
        ElMessage({ message: "加载对话失败", type: "error" });
      }
    },
    scrollChat() {
      this.$nextTick(() => {
        const box = this.$refs.chatBox;
        if (box) box.scrollTop = box.scrollHeight;
      });
    },
    async sendReply() {
      const content = (this.chatInput || "").trim();
      if (!content) return;
      this.chatSending = true;
      try {
        const res = await this.$axios({
          url: "notice/admin/reply",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({ applyId: this.chat.apply.id, content }),
          },
        });
        if (res.code === 200) {
          this.chat.list.push({
            role: "admin",
            content,
            senderName: "平台",
            createTime: new Date().toLocaleString(),
          });
          this.chatInput = "";
          this.scrollChat();
          this.getList();
          ElMessage({ message: "已回复，用户将收到站内信", type: "success" });
        } else {
          ElMessage({ message: res.msg || "回复失败", type: "error" });
        }
      } catch (e) {
        ElMessage({ message: "回复失败", type: "error" });
      }
      this.chatSending = false;
    },
    async closeChat() {
      if (!this.chat.apply) return;
      try {
        await ElMessageBox.confirm("结束该对话后用户将无法继续追问，确认结束？", "结束对话");
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "notice/admin/reply",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({
              applyId: this.chat.apply.id,
              content: "平台已结束该对话，如需继续请重新提交反馈。",
              title: "close",
            }),
          },
        });
        if (res.code === 200) {
          this.chatVisible = false;
          this.getList();
          ElMessage({ message: "对话已结束", type: "success" });
        }
      } catch (e) {
        ElMessage({ message: "操作失败", type: "error" });
      }
    },
    typeName(t) {
      return this.typeMap[t] || t || "其他";
    },
    switchTab(id) {
      if (this.currentTab === id) return;
      this.currentTab = id;
      this.status = 0;
      this.searchKey = "";
      this.page = 1;
      this.getList();
    },
    changeStatus(v) {
      this.status = v;
      this.page = 1;
      this.getList();
    },
    refreshList() {
      this.getList();
    },
    handleSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.page = 1;
        this.getList();
      }, 400);
    },
    changePage(p) {
      this.page = p;
      this.getList();
    },
    async getList() {
      this.loading = true;
      try {
        const url =
          this.currentTab === "register"
            ? "user/admin/registerlist"
            : "user/admin/applylist";
        const data = await this.$aesEncrypt({
          page: this.page,
          pageSize: this.pageSize,
          status: this.status,
          searchKey: this.searchKey,
        });
        const res = await this.$axios({
          url,
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.list = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        console.error("获取列表失败:", e);
      } finally {
        this.loading = false;
      }
    },
    async reviewRegister(u, act) {
      if (act === 1) {
        ElMessageBox.confirm(
          `确定通过「${u.name}」的注册申请吗？通过后该用户即可登录使用`,
          "通过注册",
          { confirmButtonText: "确定通过", cancelButtonText: "取消", type: "success" }
        )
          .then(() => this.doReview("user/admin/registerreview", { uid: u.uid, reviewStatus: act }))
          .catch(() => {});
      } else {
        ElMessageBox.prompt(`确定驳回「${u.name}」的注册申请吗？`, "驳回注册", {
          confirmButtonText: "确定驳回",
          cancelButtonText: "取消",
          inputPlaceholder: "驳回理由（可不填，仅供内部备注）",
          type: "warning",
        })
          .then(() =>
            this.doReview("user/admin/registerreview", { uid: u.uid, reviewStatus: act })
          )
          .catch(() => {});
      }
    },
    async reviewApply(a, act) {
      ElMessageBox.prompt(
        act === 1
          ? `通过申请「${a.title}」，回复内容将展示给申请人`
          : `驳回申请「${a.title}」，请填写驳回理由`,
        act === 1 ? "通过申请" : "驳回申请",
        {
          confirmButtonText: act === 1 ? "确定通过" : "确定驳回",
          cancelButtonText: "取消",
          inputPlaceholder: act === 1 ? "审核回复（可不填）" : "请填写驳回理由",
          inputPattern: act === 1 ? undefined : /\S/,
          inputErrorMessage: "驳回理由不能为空",
          type: act === 1 ? "success" : "warning",
        }
      )
        .then(({ value }) =>
          this.doReview("user/admin/applyreview", {
            id: a.id,
            reviewStatus: act,
            reply: value || "",
          })
        )
        .catch(() => {});
    },
    async doReview(url, payload) {
      try {
        const data = await this.$aesEncrypt(payload);
        const res = await this.$axios({
          url,
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success(res.data?.msg || res.msg || "操作成功");
          this.getList();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error("操作失败，请检查网络");
      }
    },
  },
};
</script>
